import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  libraryCompaniesQueryKeys,
  libraryCompanyQueryKeys,
} from '@/hooks/library/types'
import { useCompanyCount } from '@/hooks/library/useCompanyCount'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityCompany } from '@/types/library/Entity'

interface UseCompaniesArgs<T> {
  orgId: UUID | ''
  withConfirmed?: boolean
  withSuggested?: boolean
  select?: (data: PaginatedResponse<EntityCompany>) => T
}

interface UseCompaniesOptions<T> extends UseCompaniesArgs<T> {
  enabled?: boolean
}

const buildCompaniesUrl = (
  orgId: UUID,
  options: { withConfirmed?: boolean; withSuggested?: boolean } = {},
) => {
  const url = new URL(`${API_URL}/api/v1/organizations/${orgId}/entities`)
  url.searchParams.append('entity_types', 'company')
  if (typeof options.withConfirmed === 'boolean') {
    url.searchParams.append('with_confirmed', String(options.withConfirmed))
  }
  if (typeof options.withSuggested === 'boolean') {
    url.searchParams.append('with_suggested', String(options.withSuggested))
  }
  return url
}

export const fetchCompanies = async (
  orgId: UUID | '',
  accessToken: string,
  options: { withConfirmed?: boolean; withSuggested?: boolean } = {},
): Promise<PaginatedResponse<EntityCompany>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  const url = buildCompaniesUrl(orgId as UUID, options)
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PaginatedResponse<EntityCompany>
}

export const useCompanies = <T = EntityCompany[]>({
  orgId,
  withConfirmed,
  withSuggested,
  select,
  enabled = true,
}: UseCompaniesOptions<T> & { enabled?: boolean }) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityCompany>) => {
      if (select) {
        return select(data)
      }
      return data.data
        .slice()
        .sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? ''),
        ) as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryCompaniesQueryKeys.organization({
      orgId,
      withConfirmed,
      withSuggested,
    }),
    queryFn: () =>
      fetchCompanies(orgId, accessToken, { withConfirmed, withSuggested }),
    enabled: enabled && !!orgId && !!accessToken,
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
    select: selectFn,
  })
}

interface UseCompanyArgs<T> {
  orgId: UUID | ''
  companyId: UUID | ''
  select?: (data: EntityCompany) => T
}

const fetchCompany = async (
  orgId: UUID | '',
  companyId: UUID | '',
  accessToken: string,
): Promise<EntityCompany> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  if (!companyId) {
    throw new Error('companyId is required')
  }
  const response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/${companyId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as EntityCompany
}

export const useCompany = <T = EntityCompany>({
  orgId,
  companyId,
  select,
}: UseCompanyArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: EntityCompany) => {
      if (select) {
        return select(data)
      }
      return data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryCompanyQueryKeys.company({ orgId, companyId }),
    queryFn: () => fetchCompany(orgId, companyId, accessToken),
    enabled: !!orgId && !!companyId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}

interface UseCompanyFromCompanyListArgs {
  orgId: UUID | ''
  companyId: UUID | ''
}

export const useCompanyFromCompanyList = ({
  orgId,
  companyId,
}: UseCompanyFromCompanyListArgs) => {
  const selectFn = useCallback(
    (data: PaginatedResponse<EntityCompany>) => {
      return data.data.find((company) => company.id === companyId)
    },
    [companyId],
  )
  return useCompanies({
    orgId,
    withConfirmed: true,
    withSuggested: false,
    select: selectFn,
  })
}

interface UseSuggestedCompaniesCountArgs {
  orgId: UUID | ''
  enabled?: boolean
}

export const useSuggestedCompaniesCount = ({
  orgId,
  enabled = true,
}: UseSuggestedCompaniesCountArgs) => {
  return useCompanyCount({
    orgId,
    withConfirmed: false,
    withSuggested: true,
    enabled,
  })
}

/**
 * Selector for confirmed companies only
 */
const selectConfirmedCompanies = (data: PaginatedResponse<EntityCompany>) => {
  return data.data
    .filter((company) => company.confirmed)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
}

interface UseConfirmedCompaniesArgs {
  orgId: UUID | ''
  enabled?: boolean
}

/**
 * Hook to fetch only confirmed companies
 */
export const useConfirmedCompanies = ({
  orgId,
  enabled = true,
}: UseConfirmedCompaniesArgs) => {
  return useCompanies({
    orgId,
    withConfirmed: true,
    withSuggested: false,
    select: selectConfirmedCompanies,
    enabled,
  })
}

/**
 * Selector for company name
 */
export const selectCompanyName = (company: EntityCompany): string => {
  return company.name ?? 'Unnamed Company'
}

interface UseCompanyNameArgs {
  orgId: UUID | ''
  companyId: UUID | ''
}

/**
 * Hook to fetch just the company name (optimized for page titles)
 */
export const useCompanyName = ({ orgId, companyId }: UseCompanyNameArgs) => {
  return useCompany({ orgId, companyId, select: selectCompanyName })
}
